import React, {useEffect, useRef, useState} from 'react';
import {useRecoilValue} from 'recoil';
import {Capacitor} from '@capacitor/core';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

import Fetcher from '@/components/common/Fetcher';
import {CollectionParam, getCollections} from '@/services/magazine';
import {user} from '@/store/user';
import InstallAlert from '@/components/installAlert';
import CollectionLoader from '@/components/common/Skeleton/CollectionLoader';
import {dummyCollection} from '@/utils';
import {useScroll} from '@/hooks/useScroll';
import useStorage from '@/hooks/useStorage';

import CollectionItem from './Item';
import styles from '../styles.module.scss';

interface Props {
  tab: string;
  setTab: (t: string) => void;
}
export default function CollectionLists({tab}: Props) {
  const {syncScroll} = useScroll();
  const {setItem} = useStorage();
  const navigator = useNavigate();
  const lastElementRef = useRef<HTMLDivElement>(null);
  const {t} = useTranslation(['views'], {keyPrefix: 'collection.Lists'});

  const accessToken = useRecoilValue(user);

  const [itemList, setItemList] = useState<CollectionListItem[]>([]);
  const [lastElement, setLastElement] = useState<Element | null>(null);
  const [openInstallAlert, setOpenInstallAlert] = useState(false);
  const [listParam, setListParam] = useState<CollectionParam>({
    limit: 10,
    status: tab,
    popup_type: '',
  });
  const [totalCnt, setTotalCnt] = useState(0);
  const [limit, setLimit] = useState(10);

  const observer = new IntersectionObserver(entries => {
    const first = entries[0];
    if (first.isIntersecting) {
      if (totalCnt > limit) {
        setListParam(prev => {
          setLimit(Number(prev.limit || 0) + 10);
          return {
            ...prev,
            limit: Number(prev.limit || 0) + 10,
          };
        });
      }
    }
  });

  const toggleLike = (idx: number, isLike: boolean) => {
    const _items = [...itemList];
    _items[idx].isBookmark = isLike;
    setItemList(_items);
  };

  const toggleSetting = () => {
    if (Capacitor.getPlatform() === 'web') {
      setOpenInstallAlert(true);
    } else {
      // Echo.echo({
      //   value: 'setting',
      // });
      navigator('/push-config');
    }
  };

  const loadImage = (imgUrl: string) => {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        resolve('');
      };
      img.src = imgUrl;
    });
  };

  const loadAllImages = (images: string[]) => {
    const loadImageFnList = images.map(img => {
      return loadImage.bind(img);
    });
    return new Promise(resolve => {
      Promise.all(loadImageFnList).then(() => {
        resolve('');
      });
    });
  };

  const getData = async (args: typeof listParam | undefined) => {
    makeDummyList((args?.limit || 10) % 10);
    const res = await getCollections(accessToken, listParam);
    await loadAllImages(
      res.data.results.map((res: CollectionListItem) => {
        return res.imageUrl;
      }),
    );
    setTotalCnt(res.data.count);
    return res.data.results;
  };

  const makeDummyList = (p: number) => {
    const list: CollectionListItem[] =
      Array<CollectionListItem>(5).fill(dummyCollection);
    const _collections = [...itemList];
    setItemList(p === 0 ? [...list] : [..._collections, ...list]);
  };

  const setLastedBeforeOpenItem = async (id: string) => {
    await setItem('lastBeforeOpen', id);
  };

  useEffect(() => {
    const currentElement = lastElement;
    const currentObserver = observer;

    if (currentElement) {
      currentObserver.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement);
      }
    };
  }, [lastElement]);

  useEffect(() => {
    setLastElement(lastElementRef.current);
    if (tab === '2_before_open') {
      setLastedBeforeOpenItem(String(itemList[0] ? itemList[0].id : ''));
    }
  }, [itemList]);

  return (
    <div className={styles.collectionWrap}>
      <div className={styles.tabsArea}></div>
      <Fetcher
        fetcher={getData}
        setter={setItemList}
        args={listParam}
        syncScroll={syncScroll}
        noLoader>
        <>
          {itemList.length === 0 ? (
            <div className={styles.no_list}>
              <div className={styles.no_list_txt}>
                {t('empty1')}
                <br />
                {t('empty2')}
              </div>
              <button
                className={styles.setup_push}
                onClick={() => toggleSetting()}>
                <span className={`icon bell-icon ${styles.setup_bell}`}>
                  &nbsp;
                </span>
                <span>{t('btnPush')}</span>
              </button>
            </div>
          ) : (
            <div
              className={styles.listArea}
              style={{padding: '30px 20px 20px'}}>
              <div className={styles.list}>
                {itemList.map((item, idx) => {
                  return (
                    <div
                      key={`collection_item_${idx}`}
                      ref={idx === itemList.length - 1 ? lastElementRef : null}>
                      {item.id === -1 ? (
                        <CollectionLoader />
                      ) : (
                        <CollectionItem
                          idx={idx}
                          item={item}
                          toggleLike={toggleLike}
                          setOpenInstallAlert={setOpenInstallAlert}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      </Fetcher>
      <InstallAlert
        ignoreRead={openInstallAlert}
        setIgnore={setOpenInstallAlert}
      />
    </div>
  );
}
